import { HeartIcon } from '@heroicons/react/20/solid'
import { ChatBubbleLeftIcon, LightBulbIcon } from '@heroicons/react/24/outline'
import { Users2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { TextAreaField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'
import { formatDistanceToNow } from 'date-fns'

import { IPost } from '@/MODELS/post.model'
import { IUser } from '@/MODELS/user.model'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import { DataView } from '@/components/data-view/DataView'
import { SectionHeader } from '@/components/SectionHeader'

function CommunityPage() {
    const [newPostContent, setNewPostContent] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { user } = useAuth()

    const createPost = useCallback(
        async (get: () => Promise<void>) => {
            if (!newPostContent.trim()) return

            setIsSubmitting(true)
            try {
                await api(ROUTES.app.post, {
                    content: newPostContent,
                    owner: user,
                })

                setNewPostContent('')
                await get()
            } catch (error) {
                console.error('Failed to create post:', error)
            } finally {
                setIsSubmitting(false)
            }
        },
        [newPostContent]
    )

    return (
        <div className='min-h-screen'>
            <SectionHeader title={`Community`} subtitle='Connect, share and celebrate together' />
            <div className='max-w-2xl px-4 py-6 mx-auto'>
                <div className='p-4 mb-6 bg-white rounded-lg shadow-sm '>
                    <div className='flex items-start'>
                        <LightBulbIcon className='w-5 h-5 text-brand-400 mt-0.5 mr-3 flex-shrink-0' />
                        <div>
                            <h3 className='mb-1 text-sm font-semibold text-gray-700'>Welcome!</h3>
                            <p className='text-sm text-gray-700'>
                                This is a safe, supportive place to vent, celebrate, or connect with parents and
                                caregivers just like you.
                            </p>
                        </div>
                    </div>
                </div>

                <DataView<IPost> endpoint={ROUTES.app.post}>
                    {({ data: posts, isLoading, get: get }) => {
                        const sortedPosts = [...posts].sort(
                            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                        )

                        return (
                            <>
                                <section
                                    className='p-6 mb-6 bg-white border-neutral-200 rounded-xl'
                                    aria-label='Create a new post'
                                >
                                    <div className='space-y-4'>
                                        <TextAreaField
                                            value={newPostContent}
                                            onChange={(val) => {
                                                setNewPostContent(val)
                                            }}
                                            placeholder="What's on your mind today?"
                                            inputClass='w-full pb-4 transition-all border border-neutral-50 rounded-lg resize-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
                                            aria-label='Post content'
                                        />

                                        <button
                                            onClick={() => createPost(get)}
                                            disabled={isSubmitting}
                                            className='flex items-center  justify-center w-full px-6 py-3 space-x-2 text-sm text-white rounded-2xl bg-brand-600 hover:bg-brand-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]'
                                            aria-busy={isSubmitting}
                                        >
                                            <span>{isSubmitting ? 'Sharing...' : 'Share with Community'}</span>
                                            <Users2 className='w-4 h-4' />
                                        </button>
                                    </div>
                                </section>
                                {isLoading ? (
                                    <div className='flex items-center justify-center py-12'>
                                        <div className='w-8 h-8 border-b-2 border-blue-500 rounded-full animate-spin'></div>
                                    </div>
                                ) : (
                                    <div className='space-y-4'>
                                        {sortedPosts.length === 0 ? (
                                            <EmptyState />
                                        ) : (
                                            sortedPosts.map((post) => (
                                                <PostCard key={post._id} post={post} user={user} get={get} />
                                            ))
                                        )}
                                    </div>
                                )}
                            </>
                        )
                    }}
                </DataView>
            </div>
        </div>
    )
}

function PostCard(props: { post: IPost; user: IUser; get: () => void }) {
    const { post, user, get } = props
    const navigate = useNavigate()
    const firstComment = post.comments?.[0]

    const formatDate = (date: string | Date | undefined) => {
        if (!date) return 'Unknown'
        const parsedDate = new Date(date)
        if (isNaN(parsedDate.getTime())) return 'Unknown'
        return `${formatDistanceToNow(parsedDate)} ago`
    }

    return (
        <div
            className='p-4 transition-shadow bg-white border-gray-200 rounded-lg cursor-pointer hover:shadow-md'
            onClick={() => navigate(`/view-post/${post?._id}`)}
        >
            <div className='flex items-start mb-3 space-x-3'>
                <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm bg-gradient-to-r from-indigo-400 to-brand-400`}
                >
                    {post.owner?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className='flex-1'>
                    <div className='flex items-center mt-1 space-x-2'>
                        <span className='text-gray-900 '>{post.owner?.name || 'Anonymous'}</span>
                        <span className='w-1.5 h-1.5 bg-gray-400 rounded-full'></span>
                        <span className='text-sm text-gray-500'>{formatDate(post.createdAt)} ago</span>
                    </div>
                </div>
            </div>

            <div className='mb-4'>
                <p className='leading-relaxed text-gray-800 whitespace-pre-wrap'>{post.content}</p>
            </div>

            <PostFooter post={post} user={user} get={get} />

            {firstComment && (
                <div className='p-3 mt-2 mb-4 border-gray-100 rounded-lg '>
                    <div className='flex items-start space-x-2'>
                        <div className='flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-gray-400 to-brand-400'>
                            <span className='text-xs font-semibold text-white'>
                                {firstComment.owner?.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                        </div>
                        <div className='flex-1'>
                            <div className='flex items-center mb-1 space-x-2'>
                                <span className='text-sm font-medium text-gray-700'>
                                    {firstComment.owner?.name || 'Anonymous'}
                                </span>
                                <span className='text-xs text-gray-500'>{formatDate(firstComment?.createdAt)} ago</span>
                            </div>
                            <p className='text-sm text-gray-600 line-clamp-2'>{firstComment.body}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export const PostFooter = (props: { post: IPost; user: IUser; get: () => void }) => {
    const { post, user, get } = props
    const totalReactions = post.reactions?.reduce((sum, r) => sum + r.count, 0) || 0
    const totalComments = post.comments?.length || 0

    async function handleReact(reaction: string) {
        if (!user) return

        try {
            const res = await api(ROUTES.app.post + '/react', { postId: post?._id, reaction }).then(() => {
                get()
            })
        } catch (err) {
            console.error('Error updating reaction', err)
        }
    }

    return (
        <div className='px-6 py-4 border-b border-gray-100'>
            <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-6'>
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            handleReact('❤️')
                        }}
                        className={`flex items-center space-x-2 transition-colors ${
                            totalReactions > 0 ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                        }`}
                    >
                        <HeartIcon className='w-5 h-5' />
                        <span className='text-sm'>{totalReactions}</span>
                    </button>

                    <span className='flex items-center space-x-2 text-gray-500 transition-colors hover:text-blue-500'>
                        <ChatBubbleLeftIcon className='w-5 h-5' />
                        <span className='text-sm'>{totalComments}</span>
                    </span>
                </div>

                {post.reactions && post.reactions.length > 0 && (
                    <div className='flex items-center space-x-1'>
                        {post.reactions.length > 3 && (
                            <span className='text-sm text-gray-500'>+{post.reactions.length - 3}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export function EmptyState() {
    return (
        <div className='p-12 text-center bg-white border border-gray-200 rounded-lg shadow-sm'>
            <div className='mb-4 text-gray-400'>
                <ChatBubbleLeftIcon className='w-16 h-16 mx-auto mb-4' />
            </div>
            <h3 className='mb-2 text-lg font-medium text-gray-900'>No posts yet</h3>
            <p className='mb-6 text-gray-500'>Be the first to share something with the community!</p>
        </div>
    )
}

export default CommunityPage
